/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

const NODE_FRAGMENT_BASE = '#node=';

export const appendTreeChild = (node, parentId, nodeMap, childMap) => {
  const parent = parentId ? parentId : null;
  const childNodeIds = childMap[parentId];

  let previous_sibling = null;

  if (childNodeIds) {
    const childNodes = childNodeIds
      .map(nodeId => {
        return nodeMap[nodeId];
      })
      .filter(node => {
        return (
          node.conditions !== 'true' && node.conditions !== 'anything_else'
        );
      });

    const { length } = childNodes;

    previous_sibling = length > 0 ? childNodes[length - 1].dialog_node : null;
  }

  node.parent = parent;
  node.previous_sibling = previous_sibling;

  return node;
};

export const getFirstVisibleAncestorOrSelf = (nodeId, nodeMap) => {
  let result = nodeMap[nodeId];
  while (
    result &&
    result.type &&
    result.type !== 'standard' &&
    result.type !== 'frame' &&
    result.type !== 'folder'
  ) {
    result = nodeMap[result.parent];
  }
  return result;
};

export const getRootOrSelf = (nodeId, nodeMap) => {
  let result = nodeMap[nodeId];
  while (result && result.parent) {
    result = nodeMap[result.parent];
  }
  return result;
};

/**
 *
 * @param {Object} nodeMap - map of oll the nodes
 * @param {string} nodeId - node id
 * @returns {[]} - ids of al the ancestor nodes
 */
export const getAncestors = (nodeMap, nodeId) => {
  let node = nodeMap[nodeId];
  const ancestors = [];
  while (node.parent) {
    const parent = nodeMap[node.parent];
    ancestors.push(parent.dialog_node);
    node = parent;
  }
  return ancestors.reverse();
};

/**
 *
 * @param {Object} nodeMap - map of oll the nodes
 * @param {Object} childMap - map of child node ids
 * @param {Object} slotMap - map of slot node ids
 * @param {Object} handlerMap - map of handler node ids
 * @param {Object} responseMap - map of response node ids
 * @param {Object} node - node
 * @param {boolean} all - true - return all the descendant nodes,
 *                      - false - return just all the dialog tree nodes
 * @returns {[]} - descendant nodes and self
 */
export const getDescendants = (
  nodeMap,
  childMap,
  slotMap,
  handlerMap,
  responseMap,
  node,
  all
) => {
  const dialogMaps = {
    nodeMap,
    childMap,
    responseMap,
    handlerMap,
    slotMap
  };

  const descendants = [];

  gatherNodes(dialogMaps, descendants, [node], all);
  return descendants;
};

/**
 *
 * @param {Object} nodeMap - map of all the nodes
 * @param {Object} slotMap - map of slot node ids
 * @param {Object} handlerMap - map of handler node ids
 * @param {Object} responseMap - map of response node ids
 * @param {string} nodeId - node id
 * @returns {[]} - context variables gathered from - a node,
 *                                    - if mcrs - the responses,
 *                                    - if slots - the slot handlers
 *               - if no nodeId passed in, return all the context variables gathered from all the nodes in nodeMap
 */

export const getContextSet = (
  nodeMap,
  slotMap,
  handlerMap,
  responseMap,
  nodeId
) => {
  let vars = {};

  const dialogMaps = {
    nodeMap,
    slotMap,
    handlerMap,
    responseMap
  };

  if (nodeId) {
    // Get context for specified node
    vars = getContextSetInNode(dialogMaps, nodeId);
  } else {
    // Get context for all nodes
    for (const id in dialogMaps.nodeMap) {
      const context = getContextSetInNode(dialogMaps, id);
      Object.assign(vars, context);
    }
  }

  const result = [];
  for (const property in vars) {
    result.push(property);
  }
  return result;
};

const getContextSetInNode = (dialogMaps, nodeId) => {
  const contextSet = {};

  const { nodeMap, responseMap, handlerMap, slotMap } = dialogMaps;

  getContextSetInNodes(contextSet, nodeMap, [nodeId]);

  if (_.get(nodeMap[nodeId], 'metadata._customization.mcr')) {
    getContextSetInNodes(contextSet, nodeMap, responseMap[nodeId]);
  }

  getContextSetInNodes(contextSet, nodeMap, handlerMap[nodeId]);

  const slots = slotMap[nodeId];
  if (slots) {
    for (let i = 0; i < slots.length; i++) {
      getContextSetInNodes(contextSet, nodeMap, handlerMap[slots[i]]);
    }
  }

  return contextSet;
};

/**
 *
 * @returns {string} a node_id obtained from the URL node hash
 */

export const getNodeIdFromHash = () => {
  const hash = decodeURIComponent(window.location.hash);
  let nodeHash;
  if (hash && hash.startsWith(NODE_FRAGMENT_BASE)) {
    nodeHash = hash.replace(NODE_FRAGMENT_BASE, '');
  }
  return nodeHash;
};

/**
 *
 * Sets node hash as #node=${node_id}
 *
 * @param nodeId
 */

export const setNodeHash = nodeId => {
  window.location.hash = nodeId
    ? NODE_FRAGMENT_BASE + encodeURIComponent(nodeId)
    : '';
};

// Recursive function to gather all descendant nodes
// all=false: just include child nodes
// all=true: include related slots/handlers/responses
const gatherNodes = (dialogMaps, result, nodes, all) => {
  for (var i = 0; i < nodes.length; i++) {
    result.push(nodes[i]);
    const childrenIds = dialogMaps.childMap[nodes[i].dialog_node];
    const children = idsToNodes(childrenIds, dialogMaps.nodeMap);
    if (children) {
      gatherNodes(dialogMaps, result, children, all);
    }
    if (all) {
      const subNodesIds = getSubNodes(dialogMaps, nodes[i].dialog_node);
      const subNodes = idsToNodes(subNodesIds, dialogMaps.nodeMap);
      if (subNodes) {
        gatherNodes(dialogMaps, result, subNodes, all);
      }
    }
  }
  return result;
};

const idsToNodes = (ids, nodeMap) => {
  if (!ids) {
    return undefined;
  }
  const result = [];
  for (const id of ids) {
    const node = nodeMap[id];
    result.push(node);
  }
  return result;
};

const getSubNodes = (dialogMaps, nodeId) => {
  var result = [];
  if (dialogMaps.slotMap[nodeId]) {
    const slotIds = dialogMaps.slotMap[nodeId];
    result.push(...slotIds);
  }
  if (dialogMaps.handlerMap[nodeId]) {
    const handlerIds = dialogMaps.handlerMap[nodeId];
    result.push(...handlerIds);
  }
  if (dialogMaps.responseMap[nodeId]) {
    const responseIds = dialogMaps.responseMap[nodeId];
    result.push(...responseIds);
  }
  return result.length ? result : null;
};

const getContextSetInNodes = (contextSet, nodeMap, nodeIds) => {
  if (!nodeIds) {
    return;
  }
  for (let i = 0; i < nodeIds.length; i++) {
    const node = nodeMap[nodeIds[i]];
    if (node && node.context) {
      for (const property in node.context) {
        contextSet[property] = true;
      }
    }
  }
};
export const getCallout = actions => {
  return getWebhookCallout(actions) || getActionCallout(actions);
};

// for action skill callout only
export const getActionCallout = actions => {
  actions = actions || [];
  const result = actions.filter(action => action.type === 'assistant_action');
  if (result.length > 0) {
    return result[0];
  } else {
    return null;
  }
};

// for webhooks callout only
export const getWebhookCallout = actions => {
  actions = actions || [];
  const result = actions.filter(action => action.type === 'webhook');
  if (result.length > 0) {
    return result[0];
  } else {
    return null;
  }
};

export const checkForArtifactInCondition = (
  conditionString,
  intentOrEntity
) => {
  if (!conditionString || !intentOrEntity) {
    return null;
  }

  // Intent/entity short form
  if (conditionString.indexOf(intentOrEntity) >= 0) {
    return intentOrEntity;
  }
  // Intent long form
  var intentLongFormRegex = new RegExp(
    '(.*)(.intent\\s?==\\s?)(\'|")(' +
      intentOrEntity.substring(1) +
      ')(\'|")(.*)'
  );
  if (intentLongFormRegex.test(conditionString)) {
    return intentOrEntity;
  }

  // Entity long form
  var entityLongFormRegex = new RegExp(
    '(.*)(entities.)(' + intentOrEntity.substring(1) + ')(.*)'
  );
  if (entityLongFormRegex.test(conditionString)) {
    return intentOrEntity;
  }
  return null;
};

export const buildCalloutOptionsUrl = baseUrl => {
  // Take into account that the dialog URL may end with "/dialog/dialog#node=<node_id>"
  // or simply "/dialog/dialog"; in either case, we want to replace from the last
  // "/dialog" until the end with "/options/webhooks"
  return _.replace(baseUrl, /(\/dialog#.*$|\/dialog$)/, '/options/webhooks');
};
